<template lang="pug">
  .summary-table
    //- v1 kept keyword/sentence pairs and no member messages, so it keeps the
    //- table it always had. Only v2 rows can be opened.
    aiq-table.topics-scrollable(v-if="isLegacyCatalog"
                                :data="filteredItems"
                                v-loading="loading"
                                empty-text="No topics for this period")
      aiq-table-column(label="Topic"
                        prop="name"
                        width="120"
                        :sortable="true"
                        label-class-name="is-bordered")
      aiq-table-column(label="Keywords" width="" label-class-name="is-bordered")
        template(v-slot:default="scope")
          div.el-tag-container
            aiq-tooltip(v-for="keyword, index in scope.row.keywords"
                        :key="keyword"
                        :content="scope.row.examples[index]"
                        effect="dark"
                        placement="top")
              aiq-tag {{ keyword }} &nbsp;
      aiq-table-column(label="Frequency"
                        prop="frequency"
                        width="180"
                        :sortable="true"
                        label-class-name="is-bordered")

    aiq-table.topics-scrollable.clickable(v-else
                                          :data="filteredItems"
                                          v-loading="loading"
                                          empty-text="No topics for this period"
                                          :default-sort="DEFAULT_SORT"
                                          :row-class-name="rowClassName"
                                          @row-click="selectTopic")
      aiq-table-column(label="Topic"
                        prop="name"
                        min-width="260"
                        sortable
                        label-class-name="is-bordered")
      aiq-table-column(label="Frequency"
                        prop="frequency"
                        width="140"
                        sortable
                        align="center"
                        label-class-name="is-bordered")
      aiq-table-column(label="Last seen"
                        prop="last_seen_at"
                        width="200"
                        sortable
                        label-class-name="is-bordered")
        template(v-slot:default="{ row }")
          span {{ formatDate(row.last_seen_at) }}

    .topic-detail(v-if="selectedTopic" ref="detailSection")
      .topic-detail__toolbar
        span.topic-detail__title {{ selectedTopic.name }}
        span.topic-detail__count {{ messageCountLabel }}
        .flex-spacer
        aiq-tooltip(content="Export these messages" placement="top")
          aiq-button.export(@click="exportMessages" :disabled="isEmpty(selectedTopic.messages)")
            i.iq-ico-export
        aiq-tooltip(content="Close" placement="top")
          aiq-button.close-btn(text @click="clearSelection")
            fa-icon(:icon="['fas', 'xmark']" size="lg")
      aiq-table.detail-scrollable(:data="selectedTopic.messages"
                                  empty-text="No messages for this period")
        aiq-table-column(label="Message"
                          prop="message_text"
                          min-width="420"
                          label-class-name="is-bordered")
        aiq-table-column(label="Sent"
                          prop="created_at"
                          width="200"
                          sortable
                          label-class-name="is-bordered")
          template(v-slot:default="{ row }")
            span(v-if="row.created_at") {{ formatDate(row.created_at) }}
            span.no-date(v-else) —
</template>

<script>
import { mapState } from 'vuex';
import { find, get, isEmpty } from 'lodash';
import {
  downloadAsFile,
  useTimeZone,
  getMetricsTimeZoneAbbr,
  formatDateWithTimezone,
} from '@/libs';
import { I18N_PREFIX, TOPICS_V1 } from './constants';

const DEFAULT_SORT = { prop: 'frequency', order: 'descending' };
const DATE_FORMAT = 'MMM DD, YYYY hh:mm A';

export default {
  props: {
    dateRange: {
      required: true,
      type: Array,
    },
    query: {
      required: false,
      type: String,
    },
    getReportFileName: {
      required: true,
      type: Function,
    },
  },
  emits: ['algoVersionChange'],
  computed: {
    ...mapState({
      topicsV2Enabled: state => state.featureFlags.FEATURE_FLAGS.CONVERSATION_TOPICS_V2,
    }),
    isLegacyCatalog() {
      return this.algoVersion === TOPICS_V1;
    },
    filteredItems() {
      if (!this.query) {
        return this.items;
      }
      const needle = this.query.toLowerCase();
      // v1 shows keywords as the main content, so they stay searchable there.
      // Using '  ' as delimiter to distinguish from normally used whitespace.
      const haystack = this.isLegacyCatalog
        ? d => `${d.name}  ${(d.keywords || []).join('  ')}`
        : d => `${d.name}`;
      return this.items.filter(d => haystack(d).toLowerCase().includes(needle));
    },
    selectedTopic() {
      if (this.selectedTopicId === null) return null;
      return find(this.filteredItems, { topic_id: this.selectedTopicId }) || null;
    },
    messageCountLabel() {
      const total = get(this.selectedTopic, 'message_count', 0);
      // The table below lists at most ten; say so rather than implying it is all.
      const shown = get(this.selectedTopic, 'messages', []).length;
      const noun = total === 1 ? 'message' : 'messages';
      return total > shown
        ? `${total} ${noun} · showing latest ${shown}`
        : `${total} ${noun}`;
    },
  },
  data() {
    return {
      DEFAULT_SORT,
      items: [],
      loading: true,
      selectedTopicId: null,
      // v1 until the response says otherwise: the flag defaults off, and the
      // older table is the safer thing to render against an unknown shape.
      algoVersion: TOPICS_V1,
      timezone: '',
      tzAbbr: '',
    };
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    this.tzAbbr = getMetricsTimeZoneAbbr(this.timezone);
    this.loadMetric();
  },
  watch: {
    dateRange(value, oldValue = []) {
      const [nStart, nEnd] = value;
      if (nStart && nEnd && nStart === nEnd) {
        return;
      }
      const [start, end] = oldValue;
      if (!start && end) {
        return;
      }
      this.loadMetric();
    },
  },
  methods: {
    isEmpty,
    formatDate(d) {
      if (!d) return '';
      return `${formatDateWithTimezone(d, this.timezone, DATE_FORMAT)} ${this.tzAbbr}`;
    },
    async loadMetric() {
      const [period_start, period_end] = this.dateRange;
      if (!(period_start && period_end)) {
        return;
      }
      this.loading = true;
      this.clearSelection();
      try {
        const data = await this.$store.dispatch('metrics/getMetric', ['topics', {
          period_start,
          period_end,
          bucket: 'hour',
          // The rollout switch. Absent or false and the API answers from the
          // v1 catalog, whatever the date range.
          topics_v2: this.topicsV2Enabled,
        }]);
        this.onDataChange(data);
      } catch (err) {
        this.$aiq.notify.error(this.$t(`${I18N_PREFIX}.errors.load_failed`));
        this.onDataChange(null);
      } finally {
        this.loading = false;
      }
    },
    onDataChange(data = {}) {
      this.items = get(data, 'dataset.0.data_points', []);
      this.algoVersion = get(data, 'topics_algo_version', TOPICS_V1);
      this.$emit('algoVersionChange', {
        version: this.algoVersion,
        v2Enabled: this.topicsV2Enabled,
      });
    },
    selectTopic(row) {
      this.selectedTopicId = this.selectedTopicId === row.topic_id ? null : row.topic_id;
      if (this.selectedTopicId !== null) {
        this.scrollToDetail();
      }
    },
    scrollToDetail() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.$refs.detailSection?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });
    },
    clearSelection() {
      this.selectedTopicId = null;
    },
    rowClassName({ row }) {
      return row.topic_id === this.selectedTopicId ? 'is-selected' : '';
    },
    exportTable() {
      const rows = this.isLegacyCatalog
        ? this.filteredItems.map(t => ({
          Topic: t.name,
          Keywords: (t.keywords || []).join(', '),
          Frequency: t.frequency,
        }))
        : this.filteredItems.map(t => ({
          Topic: t.name,
          Frequency: t.frequency,
          'Last seen': this.formatDate(t.last_seen_at),
        }));
      downloadAsFile({
        filename: this.getReportFileName('Topics'),
        dataType: 'table',
        data: rows,
      });
    },
    exportMessages() {
      const topic = this.selectedTopic;
      if (!topic) return;
      downloadAsFile({
        filename: this.getReportFileName(`Topic ${topic.name}`),
        dataType: 'table',
        data: get(topic, 'messages', []).map(m => ({
          Message: m.message_text,
          Sent: this.formatDate(m.created_at),
        })),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.summary-table {
  display: flex;
  flex-direction: column;
}

.topic-detail {
  margin-top: 16px;
  border-top: 1px solid #DCDFE6;
  padding-top: 12px;

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__title {
    font-weight: 600;
  }

  &__count {
    font-weight: 400;
    color: #909399;
  }

  .flex-spacer {
    flex-grow: 1;
  }

  .el-button.export {
    height: 30px;
    width: 40px;
    padding: 0;
    margin-left: 16px;
  }

  .no-date {
    color: #C0C4CC;
  }
}
</style>

<style lang="scss">
@import "../../../../../styles/aiq-mixins.scss";

.topics-scrollable .el-table__body-wrapper {
  @include scrollable(calc(100vh - 420px));
  height: auto;
  max-height: calc(100vh - 420px);
}

.topics-scrollable.clickable .el-table__row {
  cursor: pointer;
}

.topics-scrollable .el-table__row.is-selected td {
  background-color: #ECF5FF;
}

.detail-scrollable .el-table__body-wrapper {
  @include scrollable(240px);
  height: auto;
  max-height: 240px;
}
</style>
